<template>
	<div v-if="decoratedBanner.text">
		<MessageBar
			:type="decoratedBanner.bannerType"
			header
			:class="$style['message-bar']"
			:show-close="true"
			:no-radius="true"
		>
			<div :class="$style['message-bar__content']">
				<EditorContent
					:class="$style['banner-content']"
					:editor="text"
				/>
				<a
					v-if="decoratedBanner.text.subText?.content?.length"
					:class="$style['sub-text']"
					href="#"
					@click.prevent="showPopUpInfo = true"
				>{{ $t('common.fields.more-info') }}</a>
			</div>
		</MessageBar>
		<PopUpInfo
			v-if="showPopUpInfo && decoratedBanner.text.subText?.content?.length"
			@closed="showPopUpInfo = false"
		>
			<EditorContent
				:class="$style['banner-content']"
				:editor="subText"
			/>
		</PopUpInfo>
	</div>
</template>

<script>
import PopUpInfo from '@/components/Modals/PopUpInfo'
import MessageBar from '@/components/Essentials/MessageBar'
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import { CustomLink } from '@/components/Essentials/RichText/CustomExtensions'


export default {
	name: 'Banner',
	components: {
		PopUpInfo,
		MessageBar,
		EditorContent
	},
	props: {
		banner: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			showPopUpInfo: false,
			text: null,
			subText: null
		}
	},
	computed: {
		decoratedBanner () {
			return {
				...this.banner,
				bannerType: this.parseBannerType(this.banner.bannerType),
				text: {
					text: this.parseBannerContent(this.banner.text.text),
					subText: this.parseBannerContent(this.banner.text.subText)
				}
			}
		}
	},

	watch: {
		'decoratedBanner.text.text': {
			immediate: true,
			handler (val) {
				if (!this.text) {
					this.text = this.generateEditor()
				}
				if (this.text) {
					this.text.commands.setContent(val || '<p></p>')
				}
			}
		},

		'decoratedBanner.text.subText': {
			immediate: true,
			handler (val) {
				if (!this.subText) {
					this.subText = this.generateEditor()
				}
				if (this.subText) {
					this.subText.commands.setContent(val || '<p></p>')
				}
			}
		}
	},

	beforeDestroy () {
		if (this.text) {
			this.text.destroy()
		}
		if (this.subText) {
			this.subText.destroy()
		}
	},

	methods: {
		parseBannerType (type) {
			switch (type) {
				case 'info':
					return 'primary'
				default:
					return type
			}
		},

		generateEditor (content = null) {
			return new Editor({
				editable: false,
				extensions: [
					StarterKit.configure({
						heading: { levels: [1, 2, 3] },
						horizontalRule: false,
						taskList: false,
						taskItem: false
					}),
					CustomLink.configure({
						openOnClick: false
					})
				],
				content: content || '<p></p>'
			})
		},

		parseBannerContent (json = null) {
			const content = json && json.length ? JSON.parse(json) : null
			if (content && content.content && content.content[0] && content.content[0].content) {
				return content
			}
			return null
		}
	}
}
</script>
<style module lang="scss">
.sub-text {
    margin-left: calc($gutter / 2);

    &:hover {
        text-decoration: underline;
    }
}

.banner-content {
    * {
        caret-color: currentcolor;

        &:focus {
            outline: none;
        }
    }

    p {
        margin-bottom: 0;
    }

    pre {
        padding: 0.7rem 1rem;
        border-radius: 5px;
        background: getColor(surface-elevated-high);
        color: getColor(text);
        font-size: 0.8rem;
        overflow-x: auto;

        code {
            display: block;
        }
    }

    p code {
        padding: 0.2rem 0.4rem;
        border-radius: 5px;
        font-size: 0.8rem;
        font-weight: bold;
        background: getColor(chip-light);
        color: getColor(text);
    }
}

.message-bar {
    margin-top: -#{$gutter};
    margin-right: -#{$gutter * 2};
    margin-bottom: 0;
    padding: $gutter * 1.5 $gutter * 4;

    @include media-breakpoint-only(xs) {
        padding: $gutter * 1.5;
    }

    @include media-breakpoint-down(md) {
        margin-left: -#{$gutter};
        margin-right: -#{$gutter};
    }

    &__content {
        display: flex;
        align-items: center;
    }
}
</style>
